import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Clock, MapPin, Send, Loader2, Check } from "lucide-react";
import { HeroLines, Reveal } from "../components/Reveal";
import { PESTS, PHONE_DISPLAY, PHONE_LINK, HOURS, ZONE } from "../data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const INFO = [
  { icon: Phone, label: "Téléphone", value: PHONE_DISPLAY, href: PHONE_LINK, testId: "contact-info-phone" },
  { icon: Clock, label: "Horaires", value: HOURS, testId: "contact-info-hours" },
  { icon: MapPin, label: "Zone d'intervention", value: `Cahors — ${ZONE}`, testId: "contact-info-zone" },
];

const initialForm = { name: "", email: "", phone: "", pest: "", message: "", company: "" };

export default function Contact() {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (key) => (e) => setForm({ ...form, [key]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Demande envoyée. Nous vous recontactons rapidement.");
      setForm(initialForm);
      setSent(true);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(
        typeof detail === "string"
          ? detail
          : "Envoi impossible. Appelez-nous directement au 06 72 08 86 77."
      );
    } finally {
      setSending(false);
    }
  };

  const inputCls =
    "w-full border border-black bg-white px-4 py-3 text-sm outline-none transition-colors duration-300 focus:border-[#E50914] placeholder:text-neutral-400";

  return (
    <main>
      <section data-testid="contact-header" className="hexa-bg overflow-hidden border-b border-black">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 pt-32 md:pt-40 pb-16 md:pb-20">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#E50914]">
            [ Contact ]
          </p>
          <HeroLines
            className="font-display uppercase leading-[0.9] tracking-tight text-[15vw] md:text-[9rem] mt-6"
            lines={[{ text: "Parlons-en" }]}
          />
          <Reveal delay={0.3}>
            <p className="mt-8 max-w-2xl text-base md:text-lg text-neutral-600">
              Décrivez votre situation, nous vous rappelons avec un diagnostic
              et un devis gratuit.
            </p>
          </Reveal>
        </div>
      </section>

      <section data-testid="contact-section" className="py-24 md:py-32">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 grid gap-16 lg:grid-cols-12">
          <div className="lg:col-span-5">
            <Reveal>
              <h2 className="font-display uppercase text-3xl md:text-4xl tracking-tight">
                L'unité d'intervention
              </h2>
            </Reveal>
            <div className="mt-10 border border-black">
              {INFO.map((item, i) => (
                <Reveal key={item.label} delay={i * 0.08}>
                  <div
                    data-testid={item.testId}
                    className={`flex items-start gap-4 p-6 ${i > 0 ? "border-t border-black" : ""}`}
                  >
                    <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center bg-black text-white">
                      <item.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.25em] text-neutral-500">
                        {item.label}
                      </p>
                      {item.href ? (
                        <a
                          href={item.href}
                          className="mt-1 block text-lg font-semibold transition-colors duration-300 hover:text-[#E50914]"
                        >
                          {item.value}
                        </a>
                      ) : (
                        <p className="mt-1 text-lg font-semibold">{item.value}</p>
                      )}
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.3}>
              <p className="mt-8 text-sm text-neutral-500">
                Urgence frelons, rats ou chenilles processionnaires ?
                Privilégiez l'appel : réponse immédiate pendant les horaires
                d'ouverture.
              </p>
            </Reveal>
          </div>

          <div className="lg:col-span-7">
            <Reveal delay={0.1}>
              {sent ? (
                <div
                  data-testid="contact-success-message"
                  className="border border-black p-10 md:p-14 hard-shadow text-center"
                >
                  <span className="mx-auto inline-flex h-16 w-16 items-center justify-center bg-[#E50914] text-white">
                    <Check className="h-8 w-8" />
                  </span>
                  <h3 className="mt-8 font-display uppercase text-3xl md:text-4xl tracking-tight">
                    Demande envoyée
                  </h3>
                  <p className="mt-4 text-base text-neutral-600 max-w-md mx-auto">
                    Votre demande de devis est bien arrivée. Nous vous
                    recontactons rapidement — un email de confirmation vient
                    également de vous être envoyé.
                  </p>
                  <p className="mt-6 text-sm text-neutral-500">
                    Urgence ? Appelez-nous directement au{" "}
                    <a
                      href={PHONE_LINK}
                      className="font-semibold text-[#E50914] hover:underline"
                    >
                      {PHONE_DISPLAY}
                    </a>
                  </p>
                  <button
                    type="button"
                    data-testid="contact-new-request-button"
                    onClick={() => setSent(false)}
                    className="mt-8 inline-flex items-center gap-3 border border-black bg-white px-6 py-3 text-sm font-semibold uppercase tracking-[0.1em] transition-colors duration-300 hover:bg-black hover:text-white"
                  >
                    Envoyer une autre demande
                  </button>
                </div>
              ) : (
              <form
                onSubmit={submit}
                data-testid="contact-form"
                className="border border-black p-6 md:p-10 hard-shadow"
              >
                <div className="grid gap-6 md:grid-cols-2">
                  <div>
                    <label htmlFor="name" className="text-xs font-semibold uppercase tracking-[0.2em]">
                      Nom *
                    </label>
                    <input
                      id="name"
                      data-testid="contact-name-input"
                      required
                      minLength={2}
                      value={form.name}
                      onChange={update("name")}
                      placeholder="Votre nom"
                      className={`${inputCls} mt-2`}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="text-xs font-semibold uppercase tracking-[0.2em]">
                      Email *
                    </label>
                    <input
                      id="email"
                      type="email"
                      data-testid="contact-email-input"
                      required
                      value={form.email}
                      onChange={update("email")}
                      placeholder="vous@exemple.fr"
                      className={`${inputCls} mt-2`}
                    />
                  </div>
                  <div>
                    <label htmlFor="phone" className="text-xs font-semibold uppercase tracking-[0.2em]">
                      Téléphone
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      data-testid="contact-phone-input"
                      value={form.phone}
                      onChange={update("phone")}
                      placeholder="06 00 00 00 00"
                      className={`${inputCls} mt-2`}
                    />
                  </div>
                  <div>
                    <label htmlFor="pest" className="text-xs font-semibold uppercase tracking-[0.2em]">
                      Type de nuisible
                    </label>
                    <select
                      id="pest"
                      data-testid="contact-pest-select"
                      value={form.pest}
                      onChange={update("pest")}
                      className={`${inputCls} mt-2`}
                    >
                      <option value="">Sélectionner…</option>
                      {PESTS.map((p) => (
                        <option key={p.id} value={p.name}>
                          {p.name}
                        </option>
                      ))}
                      <option value="Autre">Autre / Je ne sais pas</option>
                    </select>
                  </div>
                </div>
                <div className="mt-6">
                  <label htmlFor="message" className="text-xs font-semibold uppercase tracking-[0.2em]">
                    Votre message *
                  </label>
                  <textarea
                    id="message"
                    data-testid="contact-message-input"
                    required
                    minLength={10}
                    rows={6}
                    value={form.message}
                    onChange={update("message")}
                    placeholder="Décrivez la situation : type de nuisible, localisation, depuis quand…"
                    className={`${inputCls} mt-2 resize-none`}
                  />
                </div>
                <input
                  type="text"
                  name="company"
                  value={form.company}
                  onChange={update("company")}
                  className="hidden"
                  tabIndex={-1}
                  autoComplete="off"
                  aria-hidden="true"
                />
                <button
                  type="submit"
                  data-testid="contact-submit-button"
                  disabled={sending}
                  className="mt-8 inline-flex w-full items-center justify-center gap-3 bg-black px-7 py-4 text-sm font-semibold uppercase tracking-[0.1em] text-white transition-colors duration-300 hover:bg-[#E50914] disabled:opacity-60 md:w-auto"
                >
                  {sending ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                  {sending ? "Envoi en cours…" : "Envoyer ma demande"}
                </button>
                <p className="mt-4 text-xs text-neutral-400">
                  Vos données servent uniquement à traiter votre demande.
                </p>
              </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </main>
  );
}
